/* Per-courier Direct Integration (PCIS) credential schemas — from the credentials
   JSON Ranjeet shared (see PCIS_Courier_Credentials_Field_Reference.docx). Every
   courier has a genuinely different field set; there is no shared shape beyond
   "some kind of token/username + a few settings".

   Matching is by parent courier NAME (courierPartner/parent/list — the same list
   the Entry step's parent picker already shows, e.g. "Delhivery", "Shadowfax"),
   case-insensitive substring match. Shadowfax is the only courier here with a
   Forward/Reverse split, resolved from the Direction already captured in the
   Direction, Mode & Service Type step. A GENERIC fallback covers any courier
   without a confirmed schema yet (ATS, Bluedart, Pikndel, Proship-self, ...) so
   onboarding isn't blocked while its schema is filled in. */

const T = (key, label, opts = {}) => ({key, label, type: 'text', required: true, ...opts})
const TOGGLE = (key, label) => ({key, label, type: 'toggle', required: false})

export const DI_SCHEMAS = {
  DELHIVERY: {
    label: 'Delhivery',
    fields: [
      T('token', 'Token'),
      T('client_name', 'Client Name'),
      TOGGLE('auto_pickup_enabled', 'Auto Pickup Enabled'),
    ],
  },
  SHADOWFAX_FORWARD: {
    label: 'Shadowfax — Forward',
    fields: [
      T('token_id', 'Token Id'),
      T('order_model', 'Order Model'),
    ],
  },
  SHADOWFAX_REVERSE: {
    label: 'Shadowfax — RVP (Reverse)',
    fields: [
      T('token_id', 'Token Id'),
      T('order_model', 'Order Model'),
    ],
  },
  EKART: {
    label: 'Ekart',
    fields: [
      T('client_id', 'Client Id'),
      T('username', 'Username'),
      T('password', 'Password'),
    ],
  },
  XPRESSBEES: {
    label: 'XpressBees',
    fields: [
      T('username', 'Username'),
      T('password', 'Password'),
      T('secret_key', 'Secret Key'),
      T('xb_key', 'Xb Key'),
      T('business_account_name', 'Business Account Name'),
      T('service_type', 'Service Type'),
      T('pickup_type', 'Pickup Type'),
      T('auth_type', 'Auth Type'),
      T('order_number_format', 'Order Number Format'),
      T('auth_base_url', 'Auth Base Url'),
      T('track_base_url', 'Track Base Url'),
      T('cancel_base_url', 'Cancel Base Url'),
      T('ndr_base_url', 'Ndr Base Url'),
      T('serviceability_base_url', 'Serviceability Base Url'),
      T('npr_base_url', 'Npr Base Url'),
    ],
  },
  DTDC: {
    label: 'DTDC',
    fields: [
      T('api_key', 'Api Key'),
      T('customer_code', 'Customer Code'),
      T('x_access_token', 'X Access Token'),
      T('track_url', 'Track Url'),
    ],
  },
  TCI: {
    label: 'TCI',
    fields: [T('username', 'Username'), T('password', 'Password')],
  },
  BLITZ: {
    label: 'Blitz',
    fields: [
      T('username', 'Username'),
      T('password', 'Password'),
      T('channel_id', 'Channel Id'),
      T('auth_url', 'Auth Url'),
      T('create_order_url', 'Create Order Url'),
      T('cancel_url', 'Cancel Url'),
      T('track_url', 'Track Url'),
      T('ndr_url', 'Ndr Url'),
      T('edd_url', 'Edd Url'),
    ],
  },
  ELASTICRUN: {
    label: 'ElasticRun',
    fields: [
      T('auth_token', 'Auth Token'),
      T('org_id', 'Org Id'),
      T('user_id', 'User Id'),
    ],
  },
  GENERIC: {
    label: 'Courier (no confirmed schema yet — generic fields)',
    fields: [
      T('token', 'Token / Auth Token', {required: false}),
      T('username', 'Username', {required: false}),
      T('password', 'Password', {required: false}),
      T('business_account_name', 'Business Account Name', {required: false}),
    ],
  },
}

/* base courier keyword → schema base key. Match is a case-insensitive substring
   check against the parent's name (courierPartner/parent/list), same list the
   Entry step's parent picker already shows. */
const BASE_KEYWORDS = [
  ['XPRESSBEES', 'XPRESSBEES'],
  ['SHADOWFAX', 'SHADOWFAX'],
  ['DELHIVERY', 'DELHIVERY'],
  ['ELASTICRUN', 'ELASTICRUN'],
  ['DTDC', 'DTDC'],
  ['EKART', 'EKART'],
  ['BLITZ', 'BLITZ'],
  ['TCI', 'TCI'],
]

/* Resolves which schema to render, reusing context the wizard already has — the
   selected/created parent's name, plus the Direction captured in the Direction,
   Mode & Service Type step for Shadowfax's Forward/Reverse split. Falls back to
   GENERIC when the parent name doesn't match a confirmed schema. */
export function resolveCourierSchemaKey(parentName, direction) {
  const n = String(parentName || '').toUpperCase()
  const match = BASE_KEYWORDS.find(([kw]) => n.includes(kw))
  const base = match ? match[1] : null
  if (!base) return 'GENERIC'
  if (base === 'SHADOWFAX') return direction === 'Reverse' ? 'SHADOWFAX_REVERSE' : 'SHADOWFAX_FORWARD'
  return base
}

export function getCourierSchema(schemaKey) {
  return DI_SCHEMAS[schemaKey] || DI_SCHEMAS.GENERIC
}

/* PCIS's service_type for the courier-partners API — both reverse-with-QC and
   reverse-without-QC send RVP, everything else sends FORWARD. */
export function diServiceType(direction) {
  return direction === 'Reverse' ? 'RVP' : 'FORWARD'
}
